import type { NoyauCode } from '@rosetta/shared';

const LONGUEUR_MAX_NOM = 80;

/**
 * A file name that is safe on every system, taken from the title of the input:
 * lower case, no accent, anything that is not a letter or a digit folded into a
 * single dash. Empty when nothing usable is left.
 */
export function slugTitre(titre: string): string {
  return titre
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, LONGUEUR_MAX_NOM)
    .replace(/-+$/, '');
}

/**
 * Name of the downloaded text file: the title of the input when there is a usable one, then
 * the code of the noyau (B6, #13: the translations of two noyaux no longer share one name,
 * "retard-de-livraison-GSP.txt"), or `traduction-<code>.txt` without a title. Nothing in the
 * screen names the format.
 */
export function nomFichierTraduction(titre: string | undefined, noyau: NoyauCode): string {
  const slug = slugTitre(titre ?? '');
  return slug === '' ? `traduction-${noyau}.txt` : `${slug}-${noyau}.txt`;
}

/**
 * Name of the downloaded original text (#38): the same rule, with `origine` where the code of
 * the noyau is: "retard-de-livraison-origine.txt", or `texte-origine.txt` without a title.
 */
export function nomFichierTexteOrigine(titre: string | undefined): string {
  const slug = slugTitre(titre ?? '');
  return slug === '' ? 'texte-origine.txt' : `${slug}-origine.txt`;
}

/** Hands `texte` to the browser as a UTF-8 text file called `nom`. */
export function telechargerTexte(nom: string, texte: string): void {
  const url = URL.createObjectURL(new Blob([texte], { type: 'text/plain;charset=utf-8' }));
  const lien = document.createElement('a');
  lien.href = url;
  lien.download = nom;
  document.body.append(lien);
  lien.click();
  lien.remove();
  URL.revokeObjectURL(url);
}
